/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useSelector, useDispatch } from "react-redux";

import { showNotification } from "jazasoft";

import useQuery from "../../hooks/useQuery";
import { UPDATE_TASKS, UPDATE_LAST_SEEN, REFRESH_TASK } from "../../reducers/scheduledTaskReducer";
import { Role, TaskNames, TaskState } from "../../utils/types";
import useMutation from "../../hooks/useMutation";
import { isEmpty } from "../../utils/helpers";

const midNightMillis = new Date().setHours(0, 0, 0, 0);

const message = (task, translate) => {
  let meta = JSON.parse(task.meta || "{}");
  meta = meta.planningDto;

  let flowItems = [
    `${translate(`resources.orders.fields.poRef`, { _: "PO Ref" })}: ${meta.poRef}`,
    `${translate(`resources.orders.fields.flowInfo.productName`, { _: "Product" })}: ${meta.productName}`,
    `${translate(`resources.orders.fields.flowInfo.style`, { _: "Style" })}: ${meta.styles}`,
    `${translate(`resources.orders.fields.flowInfo.color`, { _: "PO Ref" })}: ${meta.colors}`
  ];
  if (!isEmpty(meta.inseams)) {
    flowItems.push(`${translate(`resources.orders.fields.flowInfo.inseam`, { _: "Inseam" })}: ${meta.inseams}`);
  }
  if (!isEmpty(meta.destinations)) {
    flowItems.push(`${translate(`resources.orders.fields.flowInfo.destination`, { _: "Destination" })}: ${meta.destinations}`);
  }
  if (!isEmpty(meta.deliveries)) {
    flowItems.push(`${translate(`resources.orders.fields.flowInfo.deliveryDate`, { _: "Delivery Date" })}: ${meta.deliveries}`);
  }
  if (!isEmpty(meta.flowRefs)) {
    flowItems.push(`${translate(`resources.orders.fields.flowInfo.flowRef`, { _: "Flow Ref" })}: ${meta.flowRefs}`);
  }
  if (!isEmpty(meta.customerFlowRefs)) {
    flowItems.push(`${translate(`resources.orders.fields.flowInfo.customerFlowRef`, { _: "Customer Flow Ref" })}: ${meta.customerFlowRefs}`);
  }
  if (meta.noOfLots > 1) {
    flowItems.push(`Lot: ${meta.lot}`);
  }
  if (meta.partCount > 1) {
    flowItems.push(` Part: ${meta.part}`);
  }

  const suffix = `for ${flowItems.join(", ")}`;

  const isCompleted = TaskState.COMPLETED === task.state;
  const isFailed = TaskState.FAILED === task.state;

  let msg = task.name;
  switch (task.name) {
    case TaskNames.CUT_PLAN_GENERATE:
      msg = isCompleted ? `Cut Plan Generated ${suffix}` : isFailed ? `Cut Plan Generation Failed ${suffix}` : "";
      break;
    case TaskNames.CUT_PLAN_MAIN_MARKER_UPDATE:
      msg = isCompleted ? `Main Cut Plan Updated ${suffix}` : isFailed ? `Main Cut Plan Update Failed ${suffix}` : "";

      break;
    case TaskNames.CUT_PLAN_REMNANT_MARKER_UPDATE:
      msg = isCompleted ? `Remnant Cut Plan Updated ${suffix}` : isFailed ? `Remnant Cut Plan Update Failed ${suffix}` : "";

      break;
    case TaskNames.CUT_PLAN_ADJUST:
      msg = isCompleted ? `Cut Plan Adjusted ${suffix}` : isFailed ? `Cut Plan Adjust Failed ${suffix}` : "";

      break;
    case TaskNames.CUT_PLAN_MARRY:
      msg = isCompleted ? `Cut Plan Married ${suffix}` : isFailed ? `Cut Plan Marry Failed ${suffix}` : "";

      break;
    case TaskNames.CUT_PLAN_RE_GENERATE:
      msg = isCompleted ? `Remnant Cut Plan ReGenerated ${suffix}` : isFailed ? `Remnant Cut Plan ReGenerate Failed ${suffix}` : "";

      break;
    default:
  }
  return msg;
};

const useNotification = props => {
  const { roles, translate } = props;
  const isMaster = roles.includes(Role.MASTER);
  const isSuperuser = roles.includes(Role.SUPER_USER);

  //* Redux Selector *//
  const { data = {}, lastSeenAt, lastFetchAt, version } = useSelector(state => state.scheduledTask || {});
  const authState = useSelector(state => state?.jazasoft?.auth || {});
  const dispatch = useDispatch();

  //* State *//
  const [open, setOpen] = useState(null);

  //* API call *//
  const { data: tasks = [] } = useQuery({
    url: "scheduledTasks",
    params: { search: `modifiedAt=ge=${lastFetchAt}` },
    version
  });
  const { data: user = {} } = useQuery(authState?.id && !isMaster && !isSuperuser && { url: `users/${authState?.id}` });

  const [mutate] = useMutation();

  //* Data Processing *//
  const taskList = Object.keys(data)
    .map(id => data[id])
    .sort((a, b) => b.modifiedAt - a.modifiedAt);
  // New Tasks for logged in user after last seen
  const newTaskList = taskList.filter(t => !isEmpty(lastSeenAt) && t.modifiedAt > lastSeenAt && t.createdById === authState?.id);

  const loading = newTaskList.some(e => [TaskState.NEW, TaskState.WAITING, TaskState.RUNNING].includes(e.state));
  const error = newTaskList.some(e => e.state === TaskState.FAILED);
  const badgeCount = newTaskList.filter(e => (error ? e.state === TaskState.FAILED : e.state === TaskState.COMPLETED)).length;

  //* Side Effects *//

  //* Update Tasks in Central State *//
  useEffect(() => {
    if (!isEmpty(tasks)) {
      dispatch({ type: UPDATE_TASKS, payload: { taskList: tasks } });
      const finishedTasks = tasks.filter(e => e.createdById === authState.id && [TaskState.COMPLETED, TaskState.FAILED].includes(e.state));
      // Do not show notification for while initializing
      if (lastFetchAt > midNightMillis) {
        finishedTasks.forEach(task => {
          dispatch(showNotification(message(task, translate), { type: task.state === TaskState.FAILED ? "danger" : null }));
        });
      }
    }
  }, [JSON.stringify(tasks.map(e => `${e.id}-${e.modifiedAt}`)), translate]);

  //* Update Last Seen At in Central State *//
  useEffect(() => {
    user.id && dispatch({ type: UPDATE_LAST_SEEN, payload: { lastSeenAt: user?.notificationLastSeenAt || 0 } });
  }, [user?.id, user?.notificationLastSeenAt]);

  //* Start Timer to fetch new task every one minute *//
  useEffect(() => {
    const intervalID = setInterval(() => dispatch({ type: REFRESH_TASK }), 60000);
    return () => intervalID && clearInterval(intervalID);
  }, []);

  //* If any task is in progress, check tasks every 5 seconds *//
  useEffect(() => {
    let intervalID;
    if (loading) {
      intervalID = setInterval(() => dispatch({ type: REFRESH_TASK }), 5000);
    } else {
      intervalID && clearInterval(intervalID);
    }
    return () => intervalID && clearInterval(intervalID);
  }, [loading]);

  const onOpen = event => {
    if (open && open.contains(event.target)) {
      setOpen(null);
    } else {
      setOpen(event.currentTarget);
    }
  };

  const onClose = () => {
    setOpen(null);
    if (!loading) {
      //* Update Last Seen At for User *//
      dispatch({ type: UPDATE_LAST_SEEN, payload: { lastSeenAt: new Date().getTime() } });
      if (user?.id && newTaskList.length > 0) {
        const options = {
          url: `users/${user?.id}`,
          method: "put",
          data: { ...user, notificationLastSeenAt: new Date().getTime() }
        };
        mutate(options);
      }
    }
  };

  return {
    taskList,
    loading,
    error,
    badgeCount,
    newTaskIds: newTaskList.map(e => e.id),
    open,
    onOpen,
    onClose
  };
};

export default useNotification;
